/**
 * Werkzeugpalette für den Apple Pencil.
 *
 * Aufgebaut wie die Palette in einer Notizen-App: Werkzeuge, Strichstärken,
 * Deckkraft und Farben. Zu öffnen über den Knopf am rechten Rand, über einen
 * langen Druck mit dem Stift, und in der späteren nativen App über das
 * Drücken am Apple Pencil Pro (siehe pencil.ts).
 *
 * WICHTIG: Die Palette wird EINMAL gebaut und danach nur noch umgeschaltet.
 * Würde sie bei jeder Änderung neu entstehen, flöge das Farbfeld aus dem
 * Dokument – und iPadOS schliesst den Farbwähler des Systems, sobald das
 * zugehörige Eingabefeld verschwindet. Genau das war der Grund, warum sich
 * das Farbspektrum beim Auswählen immer sofort wieder geschlossen hat.
 */
import type { DrawTool } from '../../types.ts';
import { button, h } from '../../ui/dom.ts';
import { icon } from '../../ui/icons.ts';
import {
  PALETTE,
  TOOLS,
  WIDTHS,
  getToolState,
  onToolChange,
  setToolState,
} from './tools.ts';
import { getActiveSurface, onSurfaceChange } from './surface.ts';
import { connectNativePencil, onPencilGesture } from './pencil.ts';

export interface ToolMenu {
  /** Oeffnet die Palette, optional an einer Bildschirmposition. */
  open(x?: number, y?: number): void;
  close(): void;
  destroy(): void;
}

export function mountToolMenu(): ToolMenu {
  const toggle = h(
    'button',
    {
      class: 'tool-button',
      attrs: { type: 'button', 'aria-label': 'Zeichnen', title: 'Zeichnen' },
    },
    icon('pencil'),
  );

  const panel = h('div', {
    class: 'palette',
    attrs: { hidden: 'hidden', role: 'dialog', 'aria-label': 'Werkzeuge' },
  });

  /* ------------------------------------------------- Einmal aufbauen */

  const toolRow = h('div', { class: 'palette__row palette__tools' });
  const toolButtons = TOOLS.map((tool) => {
    const el = h(
      'button',
      {
        class: 'tool-chip',
        attrs: { type: 'button', title: tool.hint },
        on: { click: () => setToolState({ tool: tool.value as DrawTool, active: true }) },
      },
      h('span', { class: `tool-chip__mark tool-chip__mark--${tool.value}` }),
      h('span', { class: 'tool-chip__label', text: tool.label }),
    );
    toolRow.append(el);
    return { value: tool.value, el };
  });

  const sizeRow = h('div', { class: 'palette__row palette__sizes' });
  const sizeButtons = WIDTHS.map((width) => {
    const dot = h('span', { class: 'size-dot__mark' });
    // Der Punkt zeigt die Strichstärke maßstäblich an.
    dot.style.width = `${Math.max(4, width.value * 1.7)}px`;
    dot.style.height = dot.style.width;
    const el = h(
      'button',
      {
        class: 'size-dot',
        attrs: { type: 'button', title: width.label, 'aria-label': width.label },
        on: { click: () => setToolState({ width: width.value, active: true }) },
      },
      dot,
    );
    sizeRow.append(el);
    return { value: width.value, el };
  });

  const opacityValue = h('span', { class: 'palette__value' });
  const opacitySlider = h('input', {
    attrs: { type: 'range', min: '10', max: '100', step: '5', 'aria-label': 'Deckkraft' },
  });
  opacitySlider.addEventListener('input', () => {
    setToolState({ opacity: Number(opacitySlider.value) / 100, active: true });
  });
  const opacityRow = h(
    'div',
    { class: 'palette__row' },
    h('span', { class: 'palette__label', text: 'Deckkraft' }),
    opacitySlider,
    opacityValue,
  );

  const colorGrid = h('div', { class: 'palette__colors' });
  const colorButtons = PALETTE.map((color) => {
    const el = h('button', {
      class: 'swatch',
      style: { background: color.value },
      attrs: { type: 'button', title: color.label, 'aria-label': `Farbe ${color.label}` },
      on: { click: () => setToolState({ color: color.value, active: true }) },
    });
    colorGrid.append(el);
    return { value: color.value, el };
  });

  /** Öffnet den Farbwähler des Systems, auf dem iPad also das Farbrad. */
  const customColor = h('input', {
    class: 'palette__wheel',
    attrs: { type: 'color', 'aria-label': 'Eigene Farbe wählen', title: 'Eigene Farbe' },
  });
  let pickerOpen = false;
  customColor.addEventListener('input', () => {
    setToolState({ color: customColor.value, active: true });
  });
  // Solange der Farbwähler offen ist, darf nichts die Palette schliessen.
  customColor.addEventListener('focus', () => {
    pickerOpen = true;
  });
  customColor.addEventListener('blur', () => {
    // Etwas Nachlauf: iPadOS meldet den Verlust des Fokus, bevor der
    // Farbwähler wirklich zu ist.
    setTimeout(() => {
      pickerOpen = false;
    }, 400);
  });
  colorGrid.append(customColor);

  const undoBtn = button('↶', () => getActiveSurface()?.undo(), {
    class: 'btn btn--icon',
    title: 'Rückgängig',
  });
  const redoBtn = button('↷', () => getActiveSurface()?.redo(), {
    class: 'btn btn--icon',
    title: 'Wiederholen',
  });
  const clearBtn = button(
    'Leeren',
    () => {
      if (confirm('Alle Zeichnungen auf diesem Bildschirm löschen?')) getActiveSurface()?.clearAll();
    },
    { class: 'btn btn--small btn--danger' },
  );
  const doneBtn = button(
    'Fertig',
    () => {
      // "Fertig" gibt die Textfelder wieder frei: der Pencil schreibt
      // danach per Scribble, statt zu zeichnen.
      setToolState({ active: false });
      close();
    },
    { class: 'btn btn--small btn--primary' },
  );
  const actionRow = h(
    'div',
    { class: 'palette__row palette__actions' },
    undoBtn,
    redoBtn,
    clearBtn,
    doneBtn,
  );

  /*
   * Finger zeichnet: fuer iPads ohne Apple Pencil (und Telefone). Dann
   * scrollt man mit zwei Fingern. Automatisch an, solange auf dem Geraet
   * noch nie ein Stift benutzt wurde.
   */
  const fingerInput = h('input', { attrs: { type: 'checkbox', role: 'switch' } });
  fingerInput.addEventListener('change', () => {
    setToolState({ finger: fingerInput.checked, fingerChosen: true, active: true });
  });
  const fingerRow = h(
    'label',
    { class: 'palette__row palette__finger' },
    fingerInput,
    h(
      'span',
      {},
      h('span', { class: 'palette__finger-title', text: 'Mit dem Finger zeichnen' }),
      h('span', { class: 'palette__finger-hint', text: 'Ohne Apple Pencil. Scrollen dann mit zwei Fingern.' }),
    ),
  );

  panel.append(toolRow, sizeRow, opacityRow, colorGrid, fingerRow, actionRow);
  document.body.append(toggle, panel);

  let open = false;

  /* ------------------------------------------------- Nur umschalten */

  function markPressed(el: HTMLElement, on: boolean, className: string): void {
    el.classList.toggle(className, on);
    el.setAttribute('aria-pressed', String(on));
  }

  function paint(): void {
    fingerInput.checked = getToolState().finger;
    const state = getToolState();

    toggle.setAttribute('aria-pressed', String(state.active));
    toggle.classList.toggle('tool-button--on', state.active);
    toggle.style.background = state.active && state.tool !== 'eraser' ? state.color : '';

    for (const entry of toolButtons) {
      markPressed(entry.el, state.active && state.tool === entry.value, 'tool-chip--on');
    }
    for (const entry of sizeButtons) {
      markPressed(entry.el, state.width === entry.value, 'size-dot--on');
    }
    for (const entry of colorButtons) {
      markPressed(entry.el, state.color.toLowerCase() === entry.value.toLowerCase(), 'swatch--on');
    }

    opacitySlider.value = String(Math.round(state.opacity * 100));
    opacityValue.textContent = `${Math.round(state.opacity * 100)} %`;
    opacityRow.hidden = state.tool === 'eraser';
    colorGrid.hidden = state.tool === 'eraser';

    // Den Wert des Farbfeldes nicht anfassen, solange der Wähler offen ist.
    if (!pickerOpen && /^#[0-9a-f]{6}$/i.test(state.color)) customColor.value = state.color;

    paintActions();
  }

  function paintActions(): void {
    const surface = getActiveSurface();
    undoBtn.disabled = !surface?.canUndo;
    redoBtn.disabled = !surface?.canRedo;
    clearBtn.disabled = !surface?.canUndo;
  }

  /* ----------------------------------------------------------- Oeffnen */

  function place(x?: number, y?: number): void {
    if (x === undefined || y === undefined) {
      panel.style.left = '';
      panel.style.top = '';
      panel.classList.add('palette--corner');
      return;
    }
    // Frei platziert (Stiftgeste): an der Stiftposition, im Bild gehalten.
    panel.classList.remove('palette--corner');
    const rect = panel.getBoundingClientRect();
    const left = Math.min(Math.max(8, x - rect.width / 2), window.innerWidth - rect.width - 8);
    const top = Math.min(Math.max(8, y - rect.height - 20), window.innerHeight - rect.height - 8);
    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
  }

  function show(x?: number, y?: number): void {
    open = true;
    panel.hidden = false;
    paint();
    place(x, y);
  }

  function close(): void {
    // Nie schliessen, solange der Farbwähler des Systems offen ist.
    if (pickerOpen) return;
    open = false;
    panel.hidden = true;
  }

  toggle.addEventListener('click', () => {
    if (open) close();
    else show();
  });

  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape' && open) close();
  };
  document.addEventListener('keydown', onKey);

  // Tippen ausserhalb schliesst die Palette, schaltet das Werkzeug aber nicht ab.
  const onOutside = (ev: PointerEvent) => {
    if (!open || pickerOpen) return;
    const target = ev.target as Node;
    if (panel.contains(target) || toggle.contains(target)) return;
    close();
  };
  document.addEventListener('pointerdown', onOutside);

  const stopToolWatch = onToolChange(() => paint());
  const stopSurfaceWatch = onSurfaceChange(() => paintActions());

  // Pencil-Geste öffnet dieselbe Palette an der Stiftposition.
  const stopPencil = onPencilGesture((gesture) => show(gesture.x, gesture.y));
  connectNativePencil();

  paint();

  return {
    open: show,
    close,
    destroy() {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onOutside);
      stopToolWatch();
      stopSurfaceWatch();
      stopPencil();
      toggle.remove();
      panel.remove();
    },
  };
}
